import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import i18n from 'i18next';

import API from '../api/api.js';
import './DestinationInsights.css';

const ACTIVITY_LABEL_IDS = {
  museums: 'activityMuseums',
  art: 'activityArt',
  daySport: 'activityDaySport',
  nightlife: 'activityNightlife',
  family: 'activityFamily',
};

function label(labels, key, t, vars) {
  var i18nKey = 'destinationInsights.' + key;
  var template = i18n.exists(i18nKey)
    ? t(i18nKey)
    : ((labels && labels[key]) || t(i18nKey));
  if (!vars || !template) {
    return template;
  }
  return Object.keys(vars).reduce(function (out, name) {
    return out.split("{{" + name + "}}").join(String(vars[name]));
  }, template);
}

function formatCurrencyExchange(exchange, code, t, labels) {
  if (!exchange) {
    return null;
  }
  const parts = [];
  if (exchange.isUsd) {
    parts.push(label(labels, 'currencyUsdNote', t));
    if (exchange.oneUsdInEur != null) {
      parts.push(label(labels, 'rateUsdEur', t, { amount: exchange.oneUsdInEur }));
    }
  } else if (exchange.isEur) {
    parts.push(label(labels, 'currencyEurNote', t));
    if (exchange.oneEurInUsd != null) {
      parts.push(label(labels, 'rateEurUsd', t, { amount: exchange.oneEurInUsd }));
    }
  } else {
    if (exchange.oneUsdInLocal != null) {
      parts.push(label(labels, 'rateUsd', t, { amount: exchange.oneUsdInLocal, code }));
    }
    if (exchange.oneEurInLocal != null) {
      parts.push(label(labels, 'rateEur', t, { amount: exchange.oneEurInLocal, code }));
    }
  }
  return parts.length ? parts.join(' · ') : null;
}

function buildQuery(travel, lang) {
  const city = travel.city.name_translations.en;
  const country = travel.country || '';
  const countryCode = travel.city.country_code || travel.airport?.country_code || '';
  const coords = travel.city.coordinates;
  const days = moment(travel.return_date).diff(moment(travel.depart_date), 'days');

  return {
    city,
    country,
    country_code: countryCode,
    depart: moment(travel.depart_date).format('YYYY-MM-DD'),
    return: moment(travel.return_date).format('YYYY-MM-DD'),
    days: Math.max(1, days),
    flight_usd: travel.value,
    lat: coords?.lat,
    lon: coords?.lon,
    lang: (lang || 'en').split('-')[0],
  };
}

function DestinationInsights({ travel }) {
  const t = i18n.t.bind(i18n);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!travel?.city) {
      setData(null);
      setError(false);
      return undefined;
    }

    let cancelled = false;
    const query = buildQuery(travel, i18n.language);

    setLoading(true);
    setError(false);
    setData(null);

    API.fetchDestinationInfo(query)
      .then((payload) => {
        if (cancelled) {
          return;
        }
        if (!payload) {
          setError(true);
          return;
        }
        setData(payload);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [travel?.id, i18n.language]);

  if (!travel?.city) {
    return null;
  }

  if (loading && !data) {
    return (
      <div className="destination-insights destination-insights--loading" aria-busy="true">
        <p className="destination-insights__status">{t('destinationInsights.loading')}</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="destination-insights destination-insights--error">
        <p className="destination-insights__status">{t('destinationInsights.unavailable')}</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { goodToKnow, about, thingsToDo, labels } = data;
  const currency = goodToKnow?.currency;
  const budget = goodToKnow?.budget;
  const weather = goodToKnow?.weather;
  const seaWater = goodToKnow?.seaWater;
  const snow = goodToKnow?.snow;
  const currencyExchangeHint = currency
    ? formatCurrencyExchange(currency.exchange, currency.code, t, labels)
    : null;
  const hasGoodToKnow =
    currency ||
    budget ||
    weather?.available ||
    seaWater?.available ||
    snow?.available;

  if (!hasGoodToKnow && !about?.summary && !(thingsToDo?.length > 0)) {
    return null;
  }

  return (
    <div className="destination-insights">
      {hasGoodToKnow && (
      <section className="destination-insights__section" aria-labelledby="dest-good-to-know">
        <h3 id="dest-good-to-know" className="destination-insights__heading">
          {label(labels, 'goodToKnow', t)}
        </h3>
        <ul className="destination-insights__facts">
          {currency && (
            <li>
              <span className="destination-insights__label">{label(labels, 'currency', t)}</span>
              <span>{currency.displayLabel || `${currency.name} (${currency.code})`}</span>
              {currencyExchangeHint && (
                <span className="destination-insights__hint">{currencyExchangeHint}</span>
              )}
            </li>
          )}
          {budget && (
            <li>
              <span className="destination-insights__label">{label(labels, 'budget', t)}</span>
              <span>
                {label(labels, 'budgetRange', t, {
                  low: budget.tripEstimateUsd.low,
                  high: budget.tripEstimateUsd.high,
                })}
              </span>
              <span className="destination-insights__hint">{label(labels, 'budgetNote', t)}</span>
            </li>
          )}
          {weather?.available && (
            <li>
              <span className="destination-insights__label">{label(labels, 'weather', t)}</span>
              <span>{weather.summary}</span>
              {weather.days?.length > 0 && (
                <ul className="destination-insights__weather-days">
                  {weather.days.map((day) => (
                    <li key={day.date}>
                      {moment(day.date).locale(i18n.language).format('ddd D MMM')}: {Math.round(day.tempMin)}–{Math.round(day.tempMax)}°C
                      {day.precipMm > 0.5 ? ` · ${label(labels, 'rainMm', t, { mm: Math.round(day.precipMm) })}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}
          {seaWater?.available && (
            <li>
              <span className="destination-insights__label">{label(labels, 'seaWater', t)}</span>
              <span>
                {seaWater.summary || label(labels, 'seaWaterRange', t, {
                  min: seaWater.tempRangeC?.low,
                  max: seaWater.tempRangeC?.high,
                })}
              </span>
              {seaWater.days?.length > 0 && (
                <ul className="destination-insights__weather-days">
                  {seaWater.days.map((day) => (
                    <li key={day.date}>
                      {moment(day.date).locale(i18n.language).format('ddd D MMM')}:{' '}
                      {Math.round(day.tempMin ?? day.tempAvg)}–{Math.round(day.tempMax ?? day.tempAvg)}°C
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}
          {snow?.available && (
            <li>
              <span className="destination-insights__label">{label(labels, 'snow', t)}</span>
              <span>{snow.summary}</span>
              {snow.days?.length > 0 && (
                <ul className="destination-insights__weather-days">
                  {snow.days.map((day) => (
                    <li key={day.date}>
                      {moment(day.date).locale(i18n.language).format('ddd D MMM')}
                      {day.snowDepthCm != null ? `: ${label(labels, 'snowOnGround', t, { cm: Math.round(day.snowDepthCm) })}` : ''}
                      {(day.snowfallCm || 0) > 0.5
                        ? ` · ${label(labels, 'snowFresh', t, { cm: Math.round(day.snowfallCm * 10) / 10 })}`
                        : ''}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          )}
        </ul>
      </section>
      )}

      {about?.summary && (
        <section className="destination-insights__section" aria-labelledby="dest-about">
          <h3 id="dest-about" className="destination-insights__heading">
            {label(labels, 'about', t, { place: about.title || travel.city.name_translations.en })}
          </h3>
          <p className="destination-insights__summary">{about.summary}</p>
          {about.wikipediaUrl && (
            <a
              className="destination-insights__wiki-link"
              href={about.wikipediaUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              {label(labels, 'wikipedia', t)}
            </a>
          )}
        </section>
      )}

      {thingsToDo?.length > 0 && (
        <section className="destination-insights__section" aria-labelledby="dest-things">
          <h3 id="dest-things" className="destination-insights__heading">
            {label(labels, 'thingsToDo', t)}
          </h3>
          <ul className="destination-insights__activities">
            {thingsToDo.map((item) => (
              <li key={item.id} className="destination-insights__activity">
                <strong>{label(labels, ACTIVITY_LABEL_IDS[item.id] || 'thingsToDo', t)}</strong>
                <span>{item.hint}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

DestinationInsights.propTypes = {
  travel: PropTypes.object,
};

export default DestinationInsights;
